import { useEffect, useState, useMemo } from "react";
import { CSSProperties } from "react";
import { Polygon, Tooltip } from "react-leaflet";
import "../styles.css";

import {
  FlyToBounds,
} from "../../../../components/Leaflet/FlyToBounds/Index";
import { SimpleButtonClose } from "../../../../components/Modal/Contents/SimpleButtonClose/Index";
import { ButtonOnlyTitle } from "../../../../components/Buttons/ButtonOnlyTitle/Index";
import ContainerZoom1X from "../../../../components/Leaflet/ContainerZoom1X/Index";
import ContainerZoom4X from "../../../../components/Leaflet/ContainerZoom4X/Index";
import ContainerZoom2X from "../../../../components/Leaflet/ContainerZoom2X/Index";
import ContainerZoom3X from "../../../../components/Leaflet/ContainerZoom3X/Index";
import { PolylineFarm } from "../../../../components/PaddocksMap/PolylineFarm/Index";
import { BatchesIcons } from "../../../../components/PaddocksMap/BatchesIcons/Index";
import { TitleModal } from "../../../../components/Modal/Contents/TitleModal/Index";
import { LabelFarm1X } from "../../../../components/PaddocksMap/LabelFarm1X/Index";
import { TextModal } from "../../../../components/Modal/Contents/TextModal/Index";
import { AnimatedZoom } from "../../../../components/Leaflet/AnimatedZoom/Index";
import { AlertModal } from "../../../../components/Modal/Alert/Index";
import { CustomControls } from "../../../../components/Leaflet/CustomControls/Index";
import { SpecsMouseOver } from "../../../../components/PaddocksMap/SpecsMouseOver/Index";

import { getCenterCoords } from "../../../../utils/leaflet/getCenterCoords";
import Constants from "../../../../constants/Index";

import { usePaddockMapData } from "../../../../hooks/usePaddockMapData";
import { usePaddockMapColors, PastureView } from "../../../../hooks/usePaddockMapColors";
import { MicroAreaTooltip } from "../../../../components/PaddocksMap/MicroAreaTooltip";
import { CapacityLegend } from "../../../../components/PaddocksMap/Legends/CapacityLegend";
import { PaddockTimeLegend } from "../../../../components/PaddocksMap/Legends/PaddockTimeLegend";
import { ManagementLegend } from "../../../../components/PaddocksMap/Legends/ManagementLegend";
import { useFilter } from "../../../../hooks/useFilter";
import { CustomToolTip } from "../../../../components/Leaflet/CustomToolTip/Index";
import { useTranslation } from "react-i18next";

const contentStyleAlert = {
  width: "440px",
  height: "218px",
  borderRadius: "20px",
  overflow: "hidden",
  zIndex: 999,
} as CSSProperties;

type PastureViewComponentProps = {
  view: PastureView;
};

export function PastureViewComponent({ view }: PastureViewComponentProps) {
  const { t } = useTranslation();
  const [rateSelected, setRateSelected] = useState(1);
  
  const {
    farms,
    areas,
    microAreas,
    batchesIcon,
    batchByMicroAreaMap,
    errorModal,
    stateOfAnimation,
    flytoBoundsRef,
    setStateOfAnimation,
    centerMap,
    toggleErrorModal,
    mapFarmFilter,
    mapAreaFilter,
  } = usePaddockMapData();

  const { microAreaFilter, batchFilter } = useFilter();

  const {
    getMicroAreaColor,
    getAreaColor,
    getFarmColor,
    getOpacity,
    getBorderColor,
    getBatchColor,
    maxValues
  } = usePaddockMapColors({
    view,
    microAreas,
    areas,
    farmFilter: mapFarmFilter,
    areaFilter: mapAreaFilter,
    microAreaFilter,
    batchFilter,
    rateSelected
  });

  useEffect(() => {
    switch (view) {
      case 'default-map':
        document.title = Constants.TITLE_PADDOCKS_MAP_INDEX;
        break;
      case 'capacity-immediate':
        document.title = Constants.TITLE_PADDOCKS_MAP_CAPACITY_IMMEDIATE;
        break;
      case 'capacity-average':
        document.title = Constants.TITLE_PADDOCKS_MAP_CAPACITY_AVAREGE;
        break;
      case 'paddock-time':
        document.title = Constants.TITLE_PADDOCKS_MAP_PADDOCK_TIME;
        break;
      case 'management-type':
      case 'management-height':
      case 'management-rate':
      case 'management-day':
        document.title = Constants.TITLE_PADDOCKS_MAP_PASTURE_MANAGEMENT;
        break;
    }
  }, [view]);

  const overrideCentermap = () => {
    setStateOfAnimation("start");
    if (mapFarmFilter) {
      centerMap(mapFarmFilter.coords);
    }
  };

  const visibleBatches = useMemo(() => {
    const filtered = batchesIcon.filter(batch => {
      if (mapAreaFilter && batch.areaId !== mapAreaFilter.id.toString()) return false;
      if (batchFilter && Number(batch.id) !== Number(batchFilter.id)) return false;
      return true;
    });
    
    const batchesWithColors = filtered.map(batch => {
      let background = "#FFF";
      
      if (view.startsWith('management-')) {
        background = getBatchColor(batch);
      }
      
      return {
        ...batch,
        background
      };
    });
    
    return batchesWithColors;
  }, [batchesIcon, mapAreaFilter, batchFilter, view, getBatchColor]);

  const maxCapacity = useMemo(() => {
    if (view === 'capacity-immediate' || view === 'capacity-average') {
      return view === 'capacity-average' 
        ? 8 
        : maxValues.maxCapacity;
    }
    return 0;
  }, [view, maxValues.maxCapacity]);

  const renderLegend = () => {
    if (view === 'default-map') {
      return null;
    }

    if (view === 'capacity-immediate' || view === 'capacity-average') {
      return (
        <CapacityLegend 
          view={view === 'capacity-immediate' ? 'immediate' : 'average'} 
          maxCapacity={maxCapacity} 
        />
      );
    }

    if (view === 'paddock-time') {
      return (
        <PaddockTimeLegend 
          maxOcupacao={maxValues.maxOcupacao} 
          maxDescanso={maxValues.maxDescanso} 
        />
      );
    }

    if (view.startsWith('management-')) {
      const managementView = view.replace('management-', '') as "type" | "height" | "rate" | "day";
      return (
        <ManagementLegend
          view={managementView}
          maxValueOnGradiente={maxValues.maxHeight}
          maxOcupacao={maxValues.maxOcupacao}
          maxDescanso={maxValues.maxDescanso}
          rateSelected={rateSelected}
          onRateChange={setRateSelected}
        />
      );
    }

    return null;
  };

  return (
    <div className="defaultmap__container">
      <AlertModal visible={errorModal} animation="slideUp" contentStyle={contentStyleAlert}>
        <section className="defaultmap__alert--content">
          <SimpleButtonClose onClick={toggleErrorModal} />
          <div className="defaultmap__alert--texts">
            <TitleModal>{Constants.MESSAGE_INVALID_MODAL_TITLE}</TitleModal>
            <TextModal>{Constants.MESSAGE_INVALID_MODAL_FILTER}</TextModal>
          </div>
          <ButtonOnlyTitle onClick={toggleErrorModal} title={t("commonUi.back")} theme="info" />
        </section>
      </AlertModal>

      <FlyToBounds ref={flytoBoundsRef} onStateAnimation={setStateOfAnimation}>
        {farms.map(farm => (
          <AnimatedZoom key={farm.id} coordsToBoundZoom={farm.map_coords || []} updateZoomVisualization={stateOfAnimation}>
            <ContainerZoom1X>
              <LabelFarm1X
                key={farm.initials}
                position={getCenterCoords(farm.map_coords || [])}
                initials={farm.initials}
              />
            </ContainerZoom1X>

            <ContainerZoom2X>
              <Polygon
                key={farm.id}
                pathOptions={{ 
                  fillColor: (view === 'capacity-immediate' || view === 'capacity-average')
                    ? getFarmColor(farm.id?.toString() || "") 
                    : Constants.LEAFLET_NORMAL_BLUE.fillColor
                }}
                positions={farm.map_coords || []}
                color={Constants.PADDOCKTIME_COLORS.WHITE}
                fillOpacity={0.55}
                fill={true}
                weight={2}
              >
                <CustomToolTip sticky>
                  <SpecsMouseOver 
                    microAreaLabel={`${farm.initials}`} 
                    type={"farm"}
                  />
                </CustomToolTip>
              </Polygon>
              <BatchesIcons batches={visibleBatches.filter(b => String(b.farmId) === String(farm.id))} />
            </ContainerZoom2X>

            <ContainerZoom3X>
              <PolylineFarm path={farm.map_coords || []} />
              <BatchesIcons batches={visibleBatches.filter(b => String(b.farmId) === String(farm.id))} />
              {areas
                .filter((area) => area.farm.id === Number(farm.id))
                .map((area) => (
                  <Polygon
                    key={area.id}
                    pathOptions={{
                      fillOpacity: getOpacity(farm.id?.toString() || "", area.id.toString()),
                      color: getBorderColor(farm.id?.toString() || "", area.id.toString()),
                      fillColor: getAreaColor(farm.id?.toString() || "", area.id.toString()),
                    }}
                    color={Constants.PADDOCKTIME_COLORS.WHITE}
                    positions={area.map_coords}
                    fill={true}
                    weight={2}
                  >
                    <CustomToolTip sticky>
                      <SpecsMouseOver
                        microAreaLabel={`${farm?.initials}-${area.initials}`}
                        type={"area"}
                      />
                    </CustomToolTip>
                  </Polygon>
                ))}
            </ContainerZoom3X>

            <ContainerZoom4X>
              <PolylineFarm path={farm.map_coords || []} />
              <BatchesIcons batches={visibleBatches.filter(b => String(b.farmId) === String(farm.id))} />
              {microAreas
                .filter((microArea) => microArea.farm.id === Number(farm.id))
                .map((microArea) => {
                  const batch = batchByMicroAreaMap.get(microArea.id);
                  
                  
                  return (
                    <Polygon
                      key={microArea.id}
                      pathOptions={{
                        fillOpacity: getOpacity(farm.id?.toString() || "", microArea.area_id.toString(), microArea.id.toString()),
                        color: getBorderColor(farm.id?.toString() || "", microArea.area_id.toString()),
                        fillColor: getMicroAreaColor(microArea),
                      }}
                      positions={microArea.map_coords}
                      fill={true}
                      weight={2}
                    >
                      <CustomToolTip sticky>
                        <div className={(() => {
                          switch (view) {
                            case 'capacity-immediate':
                            case 'capacity-average':
                              // Para capacity, usar apenas valor de lotação (não verificar management)
                              const shouldShow = !!(microArea.average_lot_rate || microArea.instant_lot_rate);
                              return shouldShow ? "havebatch" : "";
                            case 'paddock-time':
                            case 'default-map':
                              return "havebatch";
                            default:
                              // Para outras views, verificar management
                              if (!microArea.management?.type?.id || microArea.management.type.id === 1) return "";
                              
                              switch (view) {
                                case 'management-type':
                                  return "havebatch";
                                case 'management-height':
                                  return microArea.management?.type?.monitoring_types?.some(type => type.id === 2) ? "havebatch" : "";
                                case 'management-rate':
                                  return microArea.management?.type?.monitoring_types?.some(type => type.id === 1) ? "havebatch" : "";
                                case 'management-day':
                                  return microArea.management?.type?.monitoring_types?.some(type => type.id === 3) ? "havebatch" : "";
                                default:
                                  return "";
                              }
                          }
                        })()}>
                          <SpecsMouseOver
                            microAreaLabel={`${farm?.initials}-${microArea.area.initials}-${microArea.initials}`}
                            type={"batch"}
                          />
                          <MicroAreaTooltip
                            microArea={microArea}
                            view={view}
                            batchLabel={batch?.initials ? `${farm.initials}-${microArea.area.initials}-${batch.initials}` : undefined}
                            qtdLabel={batch?.animals_quantity ? `${batch.animals_quantity} animais` : undefined}
                            capacity={
                              (view === 'capacity-immediate' || view === 'capacity-average') && 
                              (microArea.average_lot_rate || microArea.instant_lot_rate) 
                                ? `${view === 'capacity-average' ? Number(microArea.average_lot_rate).toFixed(2) : Number(microArea.instant_lot_rate).toFixed(2)}` 
                                : undefined
                            }
                            acreage={
                              view === 'default-map' && microArea.acreage
                                ? Number(microArea.acreage).toFixed(2)
                                : undefined
                            }
                            maxDescanso={
                              view === 'paddock-time' ? areas.find(area => area.id === microArea.area.id)?.outdoor_grazing_time : undefined
                            }
                            maxOcupacao={
                              view === 'paddock-time' ? areas.find(area => area.id === microArea.area.id)?.indoor_grazing_time : undefined
                            }
                          />
                        </div>
                      </CustomToolTip>
                    </Polygon>
                  );
                })}
            </ContainerZoom4X>
          </AnimatedZoom>
        ))}
      </FlyToBounds>
      
      <CustomControls overrideCentermap={overrideCentermap} />
      {renderLegend()}
    </div>
  );
}
